import React from 'react';
import {
    Box,
    Card,
    Typography,
    Avatar,
    IconButton,
    Chip,
    Menu,
    MenuItem
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LockResetIcon from '@mui/icons-material/LockReset';
import { theme } from '../../../theme';

const UserCard = ({ user, onEdit, onDelete, onResetPassword, canEdit = true, canDelete = true }) => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const getRoleColor = (role) => {
        switch (role) {
            case 'admin': return theme.colors.error[500];
            case 'manager': return theme.colors.warning[500];
            case 'user': return theme.colors.primary[500];
            default: return theme.colors.grey[500];
        }
    };

    return (
        <Card
            sx={{
                p: 3,
                borderRadius: theme.borderRadius.xl,
                border: 'none',
                boxShadow: theme.boxShadow.card,
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.boxShadow.cardHover,
                }
            }}
        >
            <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
                <IconButton size="small" onClick={handleClick}>
                    <MoreVertIcon fontSize="small" sx={{ color: theme.colors.text.tertiary }} />
                </IconButton>
            </Box>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
                        mt: 1.5,
                        borderRadius: theme.borderRadius.md,
                        border: `1px solid ${theme.colors.border.light}`,
                    },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                {onEdit && canEdit && (
                    <MenuItem onClick={() => { handleClose(); onEdit(user); }} sx={{ fontSize: '0.9rem', gap: 1.5 }}>
                        <EditIcon fontSize="small" /> Éditer
                    </MenuItem>
                )}
                <MenuItem onClick={() => { handleClose(); onResetPassword && onResetPassword(user); }} sx={{ fontSize: '0.9rem', gap: 1.5, color: theme.colors.warning[600] }}>
                    <LockResetIcon fontSize="small" /> Réinitialiser MDP
                </MenuItem>
                {onDelete && canDelete && (
                    <MenuItem onClick={() => { handleClose(); onDelete(user); }} sx={{ fontSize: '0.9rem', gap: 1.5, color: theme.colors.error[500] }}>
                        <DeleteIcon fontSize="small" /> Supprimer
                    </MenuItem>
                )}
            </Menu>

            <Avatar
                src={user.avatar}
                sx={{
                    width: 80,
                    height: 80,
                    bgcolor: theme.colors.primary[50],
                    color: theme.colors.primary[600],
                    fontSize: '2rem',
                    fontWeight: 800,
                    mb: 1
                }}
            >
                {user.first_name?.[0]?.toUpperCase() || 'U'}
            </Avatar>

            <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: theme.colors.text.primary }}>
                    {user.first_name} {user.last_name}
                </Typography>
                <Typography variant="body2" sx={{ color: theme.colors.text.secondary, mb: 1 }}>
                    {user.email}
                </Typography>
                <Chip
                    label={user.role || 'User'}
                    size="small"
                    sx={{
                        bgcolor: getRoleColor(user.role) + '20',
                        color: getRoleColor(user.role),
                        fontWeight: 700,
                        textTransform: 'capitalize'
                    }}
                />
            </Box>
        </Card>
    );
};

export default UserCard;
